import {useCallback, useContext, useEffect, useRef, useState} from "react";
import firebase from "firebase/app";

import {UserContext} from "@/contexts/user";

// const BASE_URL = "http://localhost:8090";
const BASE_URL = "https://us-central1-hackernoon-app.cloudfunctions.net/hackernoonMongo2";

// Conservative in-session poll interval (120s) so a new notification can
// surface on the bell without a hard refresh, without ballooning API usage.
// Only runs while the tab is visible (see visibilitychange handling below).
const POLL_INTERVAL_MS = 120000;

const notiId = n => n?._id || n?.id;

// "Curated For You" picks are identified the same way the notifications
// dashboard does it, and are never surfaced as a toast — only real
// engagement/announcement notifications are.
const isPopupEligible = n => !!n && !n?.read && !n?.additionalData?.storyCategory;

/**
 * Keeps `user.unreadCount` in sync with the server's unread notification
 * count: one fetch on login and one every POLL_INTERVAL_MS while the tab is
 * visible. Mount once (the header) — it writes to UserContext, so every
 * consumer reads the same number.
 *
 * Also hands back the newest unread notification seen since page load
 * (`popupNoti`) so the header can toast it.
 */
const useUnreadNotificationCount = () => {
  const {user, userDispatch} = useContext(UserContext) || {};
  const uid = user?.uid;

  // Guards against an out-of-order response from a superseded fetch (e.g. a
  // logout/login in between) overwriting a newer count.
  const fetchGen = useRef(0);

  // Read inside callbacks that must not re-create on every count change.
  const unreadCountRef = useRef(0);
  unreadCountRef.current = user?.unreadCount || 0;

  const [popupNoti, setPopupNoti] = useState(null);
  // Ids already toasted this page load — reading or deleting an older
  // notification can bump a previously seen one back to the top of the unread
  // list, and that must not re-trigger the popup.
  const seenIdsRef = useRef(new Set());
  const seenUidRef = useRef(null);

  // green's UserContext exposes the raw dispatch, not a setUser helper.
  const publishCount = useCallback(
    count => {
      const next = Math.max(0, count);
      // Skip dispatch when the count hasn't changed — avoids creating a new
      // user object reference on every poll and causing unnecessary re-renders
      // in every component that reads from UserContext (e.g. ProfileForm).
      if (next !== unreadCountRef.current) {
        userDispatch?.({type: "set", payload: {unreadCount: next}});
      }
    },
    [userDispatch],
  );

  const considerForPopup = useCallback(
    noti => {
      const id = notiId(noti);
      if (!id || !isPopupEligible(noti)) return;

      if (seenUidRef.current !== uid) {
        seenIdsRef.current = new Set();
        seenUidRef.current = uid;
      }
      if (seenIdsRef.current.has(id)) return;

      seenIdsRef.current.add(id);
      setPopupNoti(noti);
    },
    [uid],
  );

  const fetchCount = useCallback(async () => {
    const gen = ++fetchGen.current;
    try {
      const {currentUser} = firebase.auth();
      if (!currentUser) return;

      const token = await currentUser.getIdToken();
      const headers = {"Content-Type": "application/json", Authorization: `Bearer ${token}`};

      // Lightweight count endpoint — counts server-side instead of pulling
      // down a page of notifications just to length-check it. It also returns
      // the newest unread item, which is all the toast needs.
      const countUrl = new URL(`${BASE_URL}/notifications/count`);
      countUrl.searchParams.append("filter", "All");

      const countReq = await fetch(countUrl.toString(), {method: "GET", headers});
      if (countReq.ok) {
        const countRes = await countReq.json();
        if (gen !== fetchGen.current) return;
        if (countRes?.ok && typeof countRes?.unreadCount === "number") {
          publishCount(countRes.unreadCount);
          considerForPopup(countRes?.latest);
          return;
        }
      }

      // Fallback for deployments where /notifications/count isn't live yet.
      const url = new URL(`${BASE_URL}/notifications`);
      url.searchParams.append("filter", "All");
      const req = await fetch(url.toString(), {method: "GET", headers});
      const res = await req.json();
      if (gen !== fetchGen.current) return;
      if (!res?.ok) return;

      const unread = (res?.notifications || []).filter(n => !n?.read);
      publishCount(typeof res?.unreadCount === "number" ? res.unreadCount : unread.length);
      considerForPopup(unread.find(isPopupEligible));
    } catch (e) {
      console.log("failed to fetch unread notification count", e);
    }
  }, [publishCount, considerForPopup]);

  const dismissPopup = useCallback(() => setPopupNoti(null), []);

  // Same endpoint + ownership/unread guards as NotificationDashboard.read().
  // Decrements the badge locally so it doesn't lag behind until the next poll.
  const markPopupRead = useCallback(
    async noti => {
      setPopupNoti(null);
      if (!noti?._id || !uid || noti?.owner !== uid || noti?.read) return;

      try {
        const {currentUser} = firebase.auth();
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        const url = new URL(`${BASE_URL}/notifications/read`);
        url.searchParams.append("_id", noti._id);

        const req = await fetch(url.toString(), {
          method: "POST",
          headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
        });
        const res = await req.json();
        if (res?.ok) publishCount(unreadCountRef.current - 1);
      } catch (e) {
        console.log("failed to mark notification as read", e);
      }
    },
    [uid, publishCount],
  );

  // Initial fetch per signed-in user.
  useEffect(() => {
    if (!uid) return;
    fetchCount();
  }, [uid, fetchCount]);

  // Drop a toast belonging to a session that just ended (logout/account switch).
  useEffect(() => {
    setPopupNoti(null);
  }, [uid]);

  // In-session polling, paused while the tab is hidden. Resumes on its normal
  // interval (no extra fetch on focus) to keep request volume predictable at
  // ~1 per POLL_INTERVAL_MS while visible.
  useEffect(() => {
    if (!uid) return;

    let intervalId = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(fetchCount, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (!intervalId) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") startPolling();
      else stopPolling();
    };

    if (document.visibilityState === "visible") startPolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [uid, fetchCount]);

  // Invalidate any in-flight fetch on unmount.
  useEffect(
    () => () => {
      fetchGen.current += 1;
    },
    [],
  );

  return {
    unreadCount: user?.unreadCount || 0,
    refetchUnreadCount: fetchCount,
    popupNoti,
    dismissPopup,
    markPopupRead,
  };
};

export default useUnreadNotificationCount;
